import useTranslation from "../../hooks/useTranslation";
import { QuestionType } from "../../types/question";
import {
  GetQuestionBreakdownResponse,
  GetSurveyChapterScoringResponse,
} from "../../types/workplaceCulture";
import { getChapterBenchmarkScore } from "../../utils/WorkplaceCulture.utils";
import { QuestionBreakdownChart } from "../QuestionBreakdownChart/QuestionBreakdownChart";
import { ScoreArc } from "../ScoreArc/ScoreArc";
import { Paper } from "../ui/Paper/Paper";
import { Typography } from "../ui/Typography/Typography";
import {
  GroupedCountItems,
  ProcessedQuestion,
  createMarginalisedDemographicGroup,
  findMarginalisedGroup,
  formatPercentageWithCount,
  processQuestionsForDisplay,
} from "./ReportQuestionBreakdown.utils";
import "./ReportQuestionBreakdown.scss";

export interface ReportQuestionBreakdownProps {
  questionsData?: GetQuestionBreakdownResponse;
  chapterScoringData?: GetSurveyChapterScoringResponse;
}

export const ReportQuestionBreakdown = ({
  questionsData,
  chapterScoringData,
}: ReportQuestionBreakdownProps) => {
  const { t } = useTranslation("reportPage");
  const { t: tWorkplace } = useTranslation("workplaceCulturePage");

  if (!questionsData) {
    return null;
  }

  const chapters = questionsData.chapters || [];
  const chapterNamesById =
    chapterScoringData?.chapters.reduce((acc, chapter) => {
      acc[chapter.id] = chapter.name;
      return acc;
    }, {} as Record<string, string>) || {};

  const renderChartQuestionItem = (question: ProcessedQuestion) => {
    const marginalisedGroup = findMarginalisedGroup(
      question.demographic_groups
    );

    if (!marginalisedGroup) {
      return null;
    }

    const marginalisedDemographicGroup =
      createMarginalisedDemographicGroup(marginalisedGroup);

    return (
      <div
        className="ReportQuestionBreakdown__chapter__content__questions__item"
        key={question.reference_id}
      >
        {/* For expanded multi-select options, show original question as title */}
        {question.isMultiSelect && question.originalQuestionText ? (
          <>
            <Typography
              desktop="body1"
              weight="bold"
              className="ReportQuestionBreakdown__chapter__content__questions__item__text"
            >
              {question.originalQuestionText}
            </Typography>
            <Typography
              desktop="body2"
              color="secondary"
              className="ReportQuestionBreakdown__chapter__content__questions__item__optionText"
              fontStyle="italic"
            >
              {question.text}
            </Typography>
          </>
        ) : (
          <Typography
            desktop="body1"
            weight="bold"
            className="ReportQuestionBreakdown__chapter__content__questions__item__text"
          >
            {question.text}
          </Typography>
        )}

        {question.preamble && !question.isMultiSelect && (
          <Typography
            desktop="body2"
            color="secondary"
            fontStyle="italic"
            className="ReportQuestionBreakdown__chapter__content__questions__item__preamble"
          >
            {question.preamble}
          </Typography>
        )}

        <QuestionBreakdownChart
          breakdownDemographicGroup={marginalisedDemographicGroup}
          useCombinedOptions={true}
          showIsSkip={true}
          isMultiSelect={question.kind === QuestionType.MultiSelect}
          customHeight={240}
          customLegendOptions={{
            enabled: true,
            layout: "horizontal",
            padding: 2,
            itemMarginTop: 4,
            itemMarginBottom: 4,
            itemStyle: {
              textOverflow: "wrap",
            },
          }}
        />
      </div>
    );
  };

  const renderCountGroups = (groupedCountItems: GroupedCountItems[]) => {
    if (groupedCountItems.length === 0) {
      return null;
    }

    return (
      <div className="ReportQuestionBreakdown__chapter__content__questions__countGroups">
        {groupedCountItems.map((group, groupIndex) => (
          <div
            key={groupIndex}
            className="ReportQuestionBreakdown__chapter__content__questions__countGroups__group"
          >
            <Typography
              desktop="body1"
              weight="bold"
              className="ReportQuestionBreakdown__chapter__content__questions__countGroups__group__title"
            >
              {group.questionText}
            </Typography>
            <div className="ReportQuestionBreakdown__chapter__content__questions__countGroups__group__list">
              {group.items.map((item: ProcessedQuestion) => (
                <div
                  key={item.reference_id}
                  className="ReportQuestionBreakdown__chapter__content__questions__countGroups__group__list__item"
                >
                  <Typography
                    desktop="body1"
                    weight="bold"
                    color="darkerStoneBlue"
                  >
                    {item.text}
                  </Typography>
                  <Typography desktop="body2">
                    {formatPercentageWithCount(
                      item.optionData?.count || 0,
                      item.total_responses?.engagement || 1
                    )}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="ReportQuestionBreakdown">
      <Typography
        tagVariant="h2"
        desktop="h2"
        className="ReportQuestionBreakdown__title"
        color="darkerStoneBlue"
      >
        {t("questionBreakdown.title")}
      </Typography>

      <div className="ReportQuestionBreakdown__chapters">
        <Paper
          shadow
          className="ReportQuestionBreakdown__chapter ReportQuestionBreakdown__chapter--description"
        >
          <Typography desktop="h3" color="darkerStoneBlue">
            {t("questionBreakdown.description.title")}
          </Typography>
          <Typography desktop="body2" color="secondary">
            {t("questionBreakdown.description.text")}
          </Typography>
        </Paper>

        {chapters.map((chapter, index) => {
          const chapterName = chapterNamesById[chapter.id];
          const chapterScore = chapterScoringData?.chapters.find(
            (scoringChapter) => scoringChapter.id === chapter.id
          );
          const benchmarkScore = getChapterBenchmarkScore(chapter.id);
          const { chartItems, groupedCountItems } = processQuestionsForDisplay(
            chapter.questions || []
          );

          // Apply page breaks: before 2nd chapter (Psychological Safety) and 3rd chapter (Authenticity)
          const shouldPageBreak = index === 1 || index === 2;

          return (
            <div
              key={chapter.id}
              className={
                shouldPageBreak
                  ? "ReportQuestionBreakdown__chapterWrapper--pageBreak"
                  : ""
              }
            >
              <Paper shadow className="ReportQuestionBreakdown__chapter">
                <Typography
                  desktop="body1"
                  className="ReportQuestionBreakdown__chapter__title"
                  color="darkerStoneBlue"
                  weight="bold"
                >
                  {chapterName || chapter.id}
                </Typography>

                {chapter.questions && chapter.questions.length > 0 && (
                  <div className="ReportQuestionBreakdown__chapter__content">
                    <div className="ReportQuestionBreakdown__chapter__content__scoreArc">
                      <ScoreArc
                        score={
                          chapterScore?.demographic.scores.total_score || 0
                        }
                        label={tWorkplace("surveyChapterOverview.scoreInfo", {
                          score: benchmarkScore,
                        })}
                        size="xSmall"
                      />
                    </div>

                    <div className="ReportQuestionBreakdown__chapter__content__questions">
                      <div className="ReportQuestionBreakdown__chapter__content__questions__grid">
                        {chartItems.map((question: ProcessedQuestion) =>
                          renderChartQuestionItem(question)
                        )}
                      </div>
                      {renderCountGroups(groupedCountItems)}
                    </div>
                  </div>
                )}
              </Paper>
            </div>
          );
        })}
      </div>
    </div>
  );
};
